// galleryService.js
const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
        return '/api';
    }
    return 'https://nlngraclub.com/nlnapi';
};

const API_BASE_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('nlngra_token');

const handleResponse = async(response) => {
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();

    if (!text || text.trim() === '') {
        throw new Error('Empty response from server');
    }

    try {
        return JSON.parse(text);
    } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response format');
    }
};

export const galleryService = {
    getMedia: async(type = 'all', category = '', limit = 24, offset = 0) => {
        try {
            const params = new URLSearchParams({
                action: 'get_media',
                type,
                category,
                limit: limit.toString(),
                offset: offset.toString(),
                _t: Date.now().toString()
            });

            const response = await fetch(`${API_BASE_URL}/gallery.php?${params}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });

            return await handleResponse(response);
        } catch (error) {
            console.error('Get media error:', error);
            return { success: false, media: [], total: 0, hasMore: false };
        }
    },

    getCategories: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=get_categories&_t=${Date.now()}`);
            return await handleResponse(response);
        } catch (error) {
            console.error('Get categories error:', error);
            return { success: false, categories: [] };
        }
    },

    getFeatured: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=get_featured&_t=${Date.now()}`);
            return await handleResponse(response);
        } catch (error) {
            console.error('Get featured error:', error);
            return { success: false, featured: [] };
        }
    },

    incrementViews: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=increment_views`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Increment views error:', error);
            return { success: false };
        }
    },

    // FIXED: adminGetMedia - Get all media for admin
    adminGetMedia: async(limit = 100, offset = 0) => {
        try {
            const params = new URLSearchParams({
                action: 'admin_list',
                limit: limit.toString(),
                offset: offset.toString(),
                _t: Date.now().toString()
            });

            const response = await fetch(`${API_BASE_URL}/gallery.php?${params}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });

            return await handleResponse(response);
        } catch (error) {
            console.error('Admin get media error:', error);
            return { success: false, media: [] };
        }
    },

    // FIXED: adminGetMediaByType - Properly filter by media type
    adminGetMediaByType: async(mediaType = 'all') => {
        try {
            const token = getToken();
            if (!token) {
                console.warn('No authentication token found');
                return { success: false, media: [] };
            }

            // Use different action or add filter parameter
            const params = new URLSearchParams({
                action: 'admin_list',
                _t: Date.now().toString()
            });

            // Add media_type filter if not 'all'
            if (mediaType !== 'all') {
                params.append('media_type', mediaType);
            }

            console.log(`📡 Fetching ${mediaType} media from admin API...`);
            const response = await fetch(`${API_BASE_URL}/gallery.php?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const result = await handleResponse(response);
            console.log(`📦 Received ${result.media?.length || 0} ${mediaType} items`);

            // Filter on client side if backend doesn't support filtering
            if (result.success && result.media && mediaType !== 'all') {
                const filteredMedia = result.media.filter(item => item.media_type === mediaType);
                console.log(`🔍 Client-side filtered to ${filteredMedia.length} ${mediaType} items`);
                return {...result, media: filteredMedia };
            }

            return result;
        } catch (error) {
            console.error('Admin get media by type error:', error);
            return { success: false, media: [] };
        }
    },

    // FIXED: adminGetImages - Specifically get only images
    adminGetImages: async() => {
        try {
            const token = getToken();
            if (!token) {
                return { success: false, media: [] };
            }

            const params = new URLSearchParams({
                action: 'admin_list',
                media_type: 'image',
                _t: Date.now().toString()
            });

            console.log('📡 Fetching images only...');
            const response = await fetch(`${API_BASE_URL}/gallery.php?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const result = await handleResponse(response);

            // Double filter to ensure only images
            if (result.success && result.media) {
                const imagesOnly = result.media.filter(item => item.media_type === 'image');
                console.log(`📸 Loaded ${imagesOnly.length} images`);
                return {...result, media: imagesOnly };
            }

            return result;
        } catch (error) {
            console.error('Admin get images error:', error);
            return { success: false, media: [] };
        }
    },

    // FIXED: adminGetVideos - Specifically get only videos
    adminGetVideos: async() => {
        try {
            const token = getToken();
            if (!token) {
                return { success: false, media: [] };
            }

            const params = new URLSearchParams({
                action: 'admin_list',
                media_type: 'video',
                _t: Date.now().toString()
            });

            console.log('📡 Fetching videos only...');
            const response = await fetch(`${API_BASE_URL}/gallery.php?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const result = await handleResponse(response);

            // Double filter to ensure only videos
            if (result.success && result.media) {
                const videosOnly = result.media.filter(item => item.media_type === 'video');
                console.log(`🎬 Loaded ${videosOnly.length} videos`);
                return {...result, media: videosOnly };
            }

            return result;
        } catch (error) {
            console.error('Admin get videos error:', error);
            return { success: false, media: [] };
        }
    },

    adminDeleteMedia: async(mediaId) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('No authentication token. Please login again.');
            }

            const response = await fetch(`${API_BASE_URL}/gallery.php?action=admin_delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({ media_id: mediaId })
            });

            return await handleResponse(response);
        } catch (error) {
            console.error('Admin delete media error:', error);
            return { success: false };
        }
    },

    adminUpdateMediaStatus: async(mediaId, status) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('No authentication token. Please login again.');
            }

            const response = await fetch(`${API_BASE_URL}/gallery.php?action=admin_update_status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({
                    media_id: mediaId,
                    status: status
                })
            });

            return await handleResponse(response);
        } catch (error) {
            console.error('Admin update media status error:', error);
            return { success: false };
        }
    },

    createMedia: async(data) => {
        try {
            const token = getToken();
            if (!token) {
                throw new Error('No authentication token. Please login again.');
            }

            const response = await fetch(`${API_BASE_URL}/gallery.php?action=create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify(data)
            });

            return await handleResponse(response);
        } catch (error) {
            console.error('Create media error:', error);
            throw error;
        }
    },

    updateMedia: async(id, data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id, ...data })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update media error:', error);
            throw error;
        }
    },

    deleteMedia: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete media error:', error);
            throw error;
        }
    },

    uploadFile: async(file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = getToken();
            if (!token) {
                throw new Error('No authentication token. Please login again.');
            }

            const response = await fetch(`${API_BASE_URL}/gallery.php?action=upload`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token
                },
                body: formData
            });

            return await handleResponse(response);
        } catch (error) {
            console.error('Upload file error:', error);
            throw error;
        }
    },

    getMediaById: async(mediaId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=get&id=${mediaId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get media by ID error:', error);
            return { success: false };
        }
    },

    getMyUploads: async() => {
        try {
            const token = getToken();
            if (!token) {
                return { success: false, media: [] };
            }

            const response = await fetch(`${API_BASE_URL}/gallery.php?action=my_uploads&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get my uploads error:', error);
            return { success: false, media: [] };
        }
    },
    // Add this inside the galleryService object
    adminBatchCreateMedia: async(items) => {
        try {
            const token = getToken();
            if (!token) throw new Error('No authentication token');
            const response = await fetch(`${API_BASE_URL}/gallery.php?action=batch_create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ items })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Batch create media error:', error);
            throw error;
        }
    },
    getPublicGallery: async(mediaType = 'all', limit = 50) => {
        try {
            const params = new URLSearchParams({
                action: 'public_list',
                media_type: mediaType,
                limit: limit.toString(),
                _t: Date.now().toString()
            });

            const response = await fetch(`${API_BASE_URL}/gallery.php?${params}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get public gallery error:', error);
            return { success: false, media: [] };
        }
    }
};

export default galleryService;